import { useCallback, useMemo } from 'react';
import { useQuery } from '@shared/react';
import { fixPaginationQuery } from './fixPaginationQuery';

interface Options {
    initialPage: number;
    initialPageSize: number;
    totalCount: number | null;
    pageQueryName: string;
    pageSizeQueryName: string;
    querySensibility: boolean;
}

interface Returned {
    queryPage: number;
    queryPageSize: number;
    setQueryPage: (page: number) => void;
    setQueryPageSize: (pageSize: number) => void;
}

export function usePaginationQuery(options: Options): Returned {
    const { initialPage, initialPageSize, totalCount, pageQueryName, pageSizeQueryName, querySensibility } = options;

    const [query, setQuery] = useQuery();

    const setQueryPage = useCallback(
        (page: number) => {
            setQuery(pageQueryName, page);
        },
        [setQuery, pageQueryName],
    );

    const setQueryPageSize = useCallback(
        (pageSize: number) => {
            setQuery(pageSizeQueryName, pageSize);
        },
        [setQuery, pageSizeQueryName],
    );

    const fixedPaginationQuery = useMemo(() => {
        if (querySensibility) {
            return fixPaginationQuery({
                initialPage,
                initialPageSize,
                totalCount,
                query,
                pageQueryName,
                pageSizeQueryName,
            });
        }
        return {
            queryPage: initialPage,
            queryPageSize: initialPageSize,
        };
    }, [query, totalCount, initialPage, initialPageSize, pageQueryName, pageSizeQueryName, querySensibility]);

    return {
        ...fixedPaginationQuery,
        setQueryPage,
        setQueryPageSize,
    };
}
